/**
 * Organizations Service
 * Handles organizations, business units, and locations management
 */

import { api } from './api'

export interface Organization {
  id: string
  name: string
  description?: string
  status: 'active' | 'inactive' | 'suspended'
  created_at: string
  updated_at: string
}

export interface BusinessUnit {
  id: string
  name: string
  description?: string
  organization_id: string
  organization?: Organization
  status: 'active' | 'inactive'
  created_at: string
  updated_at: string
}

export interface Location {
  id: string
  name: string
  address?: string
  city?: string
  state?: string
  zip_code?: string
  country?: string
  phone?: string
  email?: string
  business_unit_id: string
  business_unit?: BusinessUnit
  created_at: string
  updated_at: string
}

export interface OrganizationCreate {
  name: string
  description?: string
  status?: string
}

export interface OrganizationUpdate {
  name?: string
  description?: string
  status?: string
}

export interface BusinessUnitCreate {
  name: string
  description?: string
  organization_id: string
  status?: string
}

export interface LocationCreate {
  name: string
  address?: string
  city?: string
  state?: string
  zip_code?: string
  country?: string
  phone?: string
  email?: string
  business_unit_id: string
}

export interface OrganizationListResponse {
  organizations: Organization[]
  total: number
}

export interface BusinessUnitListResponse {
  business_units: BusinessUnit[]
  total: number
}

export interface LocationListResponse {
  locations: Location[]
  total: number
}

class OrganizationService {
  private baseUrl = '/api/v1/organizations'

  // Organizations
  async getOrganizations(): Promise<OrganizationListResponse> {
    return api.get<OrganizationListResponse>(`${this.baseUrl}`)
  }

  async getOrganization(id: string): Promise<Organization> {
    return api.get<Organization>(`${this.baseUrl}/${id}`)
  }

  async createOrganization(data: OrganizationCreate): Promise<Organization> {
    return api.post<Organization>(`${this.baseUrl}`, data)
  }

  async updateOrganization(id: string, data: OrganizationUpdate): Promise<Organization> {
    return api.put<Organization>(`${this.baseUrl}/${id}`, data)
  }

  async deleteOrganization(id: string): Promise<void> {
    await api.delete(`${this.baseUrl}/${id}`)
  }

  // Business Units
  async getBusinessUnits(organizationId?: string): Promise<BusinessUnitListResponse> {
    const params = organizationId ? `?organization_id=${organizationId}` : ''
    return api.get<BusinessUnitListResponse>(`${this.baseUrl}/business-units${params}`)
  }

  async getBusinessUnit(id: string): Promise<BusinessUnit> {
    return api.get<BusinessUnit>(`${this.baseUrl}/business-units/${id}`)
  }

  async createBusinessUnit(data: BusinessUnitCreate): Promise<BusinessUnit> {
    return api.post<BusinessUnit>(`${this.baseUrl}/business-units`, data)
  }

  async updateBusinessUnit(id: string, data: Partial<BusinessUnitCreate>): Promise<BusinessUnit> {
    return api.put<BusinessUnit>(`${this.baseUrl}/business-units/${id}`, data)
  }

  async deleteBusinessUnit(id: string): Promise<void> {
    await api.delete(`${this.baseUrl}/business-units/${id}`)
  }

  // Locations
  async getLocations(businessUnitId?: string): Promise<LocationListResponse> {
    const params = businessUnitId ? `?business_unit_id=${businessUnitId}` : ''
    return api.get<LocationListResponse>(`${this.baseUrl}/locations${params}`)
  }

  async getLocation(id: string): Promise<Location> {
    return api.get<Location>(`${this.baseUrl}/locations/${id}`)
  }

  async createLocation(data: LocationCreate): Promise<Location> {
    return api.post<Location>(`${this.baseUrl}/locations`, data)
  }

  async updateLocation(id: string, data: Partial<LocationCreate>): Promise<Location> {
    return api.put<Location>(`${this.baseUrl}/locations/${id}`, data)
  }

  async deleteLocation(id: string): Promise<void> {
    await api.delete(`${this.baseUrl}/locations/${id}`)
  }
}

export const organizationService = new OrganizationService()
